import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { businessKey, businessesKey } from "@/business/query";
import { useApi } from "@/core/api";
import { EDITOR_LIMITS, EDITOR_POLL_INTERVAL } from "./constants";
import { enrichEditorDocuments, enrichMessagePage } from "./enrich-editor";
import { enrichRoster } from "./enrich-roster";
import { isAccessError, mergeMessagePage } from "./state";
import type {
  EditorDocument,
  Message,
  MessagePage,
  SendMessage,
  PendingUpload,
  AccessKeyView,
  RosterView,
  Workspace,
} from "./types";

export const editorKey = (businessId: string) =>
  ["payroll-editor", businessId] as const;
/** Identifies the send mutation, so its state can be read from anywhere. */
export const sendKey = (businessId: string, workspaceId: string | undefined) =>
  [...editorKey(businessId), workspaceId, "send"] as const;
export const editorPath = (businessId: string) =>
  `/businesses/${businessId}/payroll_editor`;

/**
 * Everything the Payroll rules screen reads and writes.
 *
 * The workspace is never addressed by URL. There is one screen, and it shows
 * the business's current proposal, so this resolves which one that is rather
 * than being told. That is what keeps the screen continuous: starting a new
 * proposal changes what this returns, not where the reader is.
 */
export const usePayrollEditor = (businessId: string, allowed: boolean) => {
  const api = useApi();
  const client = useQueryClient();
  const base = editorPath(businessId);
  const current = useQuery({
    queryKey: [...editorKey(businessId), "current"],
    queryFn: ({ signal }) =>
      api.get<Workspace>(base, { signal }).then((r) => r.data),
    enabled: allowed,
    retry: false,
  });
  const workspaceId = current.data?.id ?? undefined;
  const path = `${base}/workspaces/${workspaceId}`;
  const key = [...editorKey(businessId), workspaceId];
  const messagesKey = [...key, "messages"];
  const documentsKey = [...key, "documents"];
  const messages = useQuery({
    queryKey: messagesKey,
    queryFn: async ({ signal }) => {
      let merged = client.getQueryData<MessagePage>(messagesKey);
      while (true) {
        const after = merged?.next_sequence ?? 0;
        const { data } = await api.get<MessagePage>(`${path}/messages`, {
          signal,
          params: { after_sequence: after },
        });
        if (
          after > 0 &&
          data.latest_request &&
          merged?.latest_request?.id !== data.latest_request.id &&
          merged?.messages.some(
            (message) =>
              message.role === "user" &&
              message.id !== data.latest_request?.id &&
              ["queued", "running", "waiting"].includes(message.status ?? ""),
          )
        ) {
          merged = undefined;
          continue;
        }
        merged = mergeMessagePage(merged, data);
        if (
          data.messages.length < EDITOR_LIMITS.messagePageSize ||
          data.next_sequence <= after
        )
          return merged;
      }
    },
    select: enrichMessagePage,
    enabled: allowed && !!workspaceId,
    retry: false,
    refetchInterval: (query) =>
      isAccessError(query.state.error)
        ? false
        : query.state.data?.workspace.active_request
          ? query.state.data.reply_preview
            ? EDITOR_POLL_INTERVAL.reply
            : EDITOR_POLL_INTERVAL.active
          : EDITOR_POLL_INTERVAL.idle,
  });
  // A completed turn can change the roster or warm a comparison after the last
  // active poll. Refresh those independent server resources at the boundary.
  const requestId = messages.data?.latest_request?.id;
  const requestStatus = messages.data?.latest_request?.status;
  useEffect(() => {
    if (
      !workspaceId ||
      !requestId ||
      !["completed", "failed", "cancelled"].includes(requestStatus ?? "")
    )
      return;
    for (const resource of ["comparison", "roster", "access-keys"])
      void client.invalidateQueries({
        queryKey: [...editorKey(businessId), workspaceId, resource],
      });
  }, [businessId, workspaceId, requestId, requestStatus, client]);
  const documents = useQuery({
    queryKey: documentsKey,
    queryFn: ({ signal }) =>
      api
        .get<{ documents: EditorDocument[] }>(`${path}/documents`, { signal })
        .then((r) => r.data.documents),
    select: enrichEditorDocuments,
    enabled: allowed && !!workspaceId && !isAccessError(messages.error),
    retry: false,
    refetchInterval: (query) =>
      isAccessError(query.state.error)
        ? false
        : query.state.data?.some((file) => file.status === "pending")
          ? EDITOR_POLL_INTERVAL.files
          : EDITOR_POLL_INTERVAL.idle,
  });
  const refresh = () =>
    client.invalidateQueries({ queryKey: editorKey(businessId) });
  const onError = (error: unknown) => {
    if (isAccessError(error)) {
      void client.invalidateQueries({ queryKey: businessKey(businessId) });
      void client.invalidateQueries({ queryKey: businessesKey });
    }
    void refresh();
  };
  const open = useMutation({
    mutationFn: () => api.post<Workspace>(base).then((r) => r.data),
    onSuccess: (workspace) =>
      client.setQueryData([...editorKey(businessId), "current"], workspace),
    onError,
  });
  // Scope serializes HTTP requests. The conversation waits for server turn
  // completion before it sends the next queued message.
  const send = useMutation({
    mutationKey: sendKey(businessId, workspaceId),
    scope: { id: `payroll-editor-send:${workspaceId ?? "none"}` },
    mutationFn: (payload: SendMessage) =>
      api
        .post<{ message: Message }>(`${path}/messages`, payload)
        .then((r) => r.data.message),
    onSuccess: () => refresh(),
    onError,
  });
  const upload = useMutation({
    mutationFn: ({ file, clientId }: PendingUpload) => {
      const form = new FormData();
      form.append("file", file);
      form.append("title", file.name.slice(0, EDITOR_LIMITS.titleLength));
      form.append("client_upload_id", clientId);
      return api
        .post<{ document: EditorDocument }>(`${path}/documents`, form)
        .then((response) => response.data.document);
    },
    onSuccess: async (document) => {
      // An older list request must not replace the newly saved attachment.
      await client.cancelQueries({ queryKey: documentsKey });
      client.setQueryData<EditorDocument[]>(documentsKey, (files) => [
        document,
        ...(files ?? []).filter((file) => file.id !== document.id),
      ]);
      await refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${path}/documents/${id}`),
    onSuccess: () => refresh(),
    onError,
  });
  // The business putting its own proposal live. It takes effect on the next
  // payroll run; there is no review step behind it.
  const publish = useMutation({
    mutationFn: () =>
      api.post<Workspace>(`${path}/publish`).then((r) => r.data),
    onSuccess: () => refresh(),
    onError,
  });
  // Throws the proposal away and opens a clean one. The script, the written
  // plan and the conversation go with it. On the roster, only the rows this
  // proposal moved are put back, because one draft roster belongs to the
  // business and somebody else's staged work may be sitting on it.
  const discard = useMutation({
    mutationFn: () =>
      api.post<Workspace>(`${path}/discard`).then((r) => r.data),
    onSuccess: () => refresh(),
    onError,
  });
  // Asks the running turn to stop. It stops at its next operation boundary, so
  // this resolves long before the transcript goes quiet.
  // Takes the request it is stopping, so a failure can be shown against that
  // request and nothing else. The error used to be held by the mutation alone,
  // which outlives the request, so a Stop that failed last week could reappear
  // beside a new one.
  const cancel = useMutation({
    mutationFn: (_requestId: string | undefined) =>
      api.delete<Workspace>(`${path}/messages/active`),
    onSuccess: () => refresh(),
    onError,
  });
  return {
    current,
    workspaceId,
    messages,
    documents,
    open,
    send,
    upload,
    remove,
    cancel,
    publish,
    discard,
    refresh,
  };
};

export type PayrollEditorQueries = ReturnType<typeof usePayrollEditor>;

/**
 * The roster behind the rules: who the plan pays and what it reads about them.
 *
 * It sits under the same query key as the rest of the editor, so the refresh
 * that follows a sent message picks it up, and it polls while a request is
 * running because a roster change can land in the middle of one.
 */
export const useEditorAccessKeys = (
  businessId: string,
  workspaceId: string | undefined,
  active: boolean,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...editorKey(businessId), workspaceId, "access-keys"],
    queryFn: ({ signal }) =>
      api
        .get<AccessKeyView>(
          `${editorPath(businessId)}/workspaces/${workspaceId}/access_keys`,
          { signal },
        )
        .then((r) => r.data),
    enabled: !!workspaceId,
    retry: false,
    refetchInterval: active ? EDITOR_POLL_INTERVAL.idle : false,
  });
};

export const useEditorRoster = (
  businessId: string,
  workspaceId: string | undefined,
  active: boolean,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...editorKey(businessId), workspaceId, "roster"],
    queryFn: ({ signal }) =>
      api
        .get<RosterView>(
          `${editorPath(businessId)}/workspaces/${workspaceId}/roster`,
          { signal },
        )
        .then((r) => r.data),
    select: enrichRoster,
    enabled: !!workspaceId,
    retry: false,
    refetchInterval: active ? EDITOR_POLL_INTERVAL.idle : false,
  });
};
